import { Button, Checkbox, Form, Input, InputNumber, message, Select, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { serverFetch } from "../../../server";
import { CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { dictsChannelVids } from "../../../extensions/dicts";

const copyToClipboard = (text, label = "Текст") => {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => message.success(`${label} скопирован`),
    () => message.error("Не удалось скопировать")
  );
};

const CHANNEL_VID_CHAT_SITE = 201; // Чат на сайте — код только для чтения

const defaultItem = { companyId: null, active: true, name: "", code: "", vid: 1, idx: 0, providerId: null };

const Channel = ({ id, onClose, companyId }) => {
  const [item, setItem] = useState(defaultItem);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    serverFetch("Message/Provider")
      .then((data) => setProviders(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const providerOptions = useMemo(() => {
    const vid = item.vid ?? 1;
    const filtered = providers.filter((p) => p.vid === vid);
    const opts = filtered.map((p) => ({ value: p.id, label: p.name }));
    return [{ value: null, label: "Не указан" }, ...opts];
  }, [providers, item.vid]);

  const selectedProvider = useMemo(
    () => (item.providerId != null ? providers.find((p) => p.id === item.providerId) : null),
    [providers, item.providerId]
  );

  const getData = () => {
    if (id) {
      setLoading(true);
      serverFetch(`Message/Channel/${id}`)
        .then((data) => {
          setLoading(false);
          setItem(data);
          form.setFieldsValue(data);
        })
        .catch((e) => {
          setLoading(false);
          message.error(e.userMessage);
        });
    } else {
      const initial = { ...defaultItem, companyId };
      form.setFieldsValue(initial);
      setItem(initial);
    }
  };

  useEffect(getData, [id, companyId]);

  const onValuesChange = (value) => {
    if ("vid" in value) {
      const newVid = value.vid ?? item.vid;
      const allowedIds = providers.filter((p) => p.vid === newVid).map((p) => p.id);
      if (item.providerId != null && !allowedIds.includes(item.providerId)) {
        form.setFieldsValue({ providerId: null });
        setItem({ ...item, ...value, providerId: null });
        return;
      }
    }
    setItem({ ...item, ...value });
  };

  const onSave = () => {
    form.validateFields().then(() => {
      setLoading(true);
      const payload = {
        ...item,
        companyId: item.companyId ?? companyId,
        name: item.name ?? "",
        code: item.code ?? "",
        vid: item.vid ?? 1,
        idx: item.idx ?? 0,
        active: item.active ?? true,
        providerId: item.providerId ?? null,
      };
      if (id) payload.id = id;
      const method = id ? "PUT" : "POST";
      serverFetch("Message/Channel", { method, bodyData: payload })
        .then(() => onClose(true))
        .catch((e) => {
          setLoading(false);
          message.error(e.userMessage);
        });
    });
  };

  return (
    <div className="sport-modal">
      <div className="sport-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose(false)}>
        <div className="sport-modal-content" style={{ width: 800 }} onMouseDown={(e) => e.stopPropagation()}>
          <div className="sport-modal-title">
            <span>{id ? "Канал" : "Новый канал"}</span>
            <CloseOutlined className="sport-section-title-close" onClick={() => onClose(false)} />
          </div>
          <Form form={form} onValuesChange={onValuesChange} labelCol={{ span: 8 }} wrapperCol={{ span: 24 }} colon={false} disabled={loading}>
            <Form.Item name="active" valuePropName="checked" label=" ">
              <Checkbox>Активный</Checkbox>
            </Form.Item>
            <Form.Item name="name" label="Наименование" rules={[{ required: true, message: "Введите наименование" }]}>
              <Input placeholder="Наименование канала" />
            </Form.Item>
            {item.vid === CHANNEL_VID_CHAT_SITE && item.code != null && item.code !== "" && (
              <>
                <Form.Item label="Код">
                  <Space.Compact style={{ width: "100%" }}>
                    <Input value={item.code} readOnly />
                    <Button type="default" icon={<CopyOutlined />} onClick={() => copyToClipboard(item.code, "Код")} title="Копировать" />
                  </Space.Compact>
                </Form.Item>
                <Form.Item label="Script">
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Input.TextArea value={item.script} readOnly rows={6} style={{ fontFamily: "monospace" }} />
                    <Button type="default" icon={<CopyOutlined />} onClick={() => copyToClipboard(item.script, "Script")}>
                      Копировать
                    </Button>
                  </Space>
                </Form.Item>
              </>
            )}
            {item.vid !== CHANNEL_VID_CHAT_SITE && (
              <>
                <Form.Item label="Код">
                  <Space.Compact style={{ width: "100%" }}>
                    <Form.Item name="code" noStyle>
                      <Input placeholder="API ключ" />
                    </Form.Item>
                    {(item.code != null && item.code !== "") && (
                      <Button type="default" icon={<CopyOutlined />} onClick={() => copyToClipboard(item.code, "Код")} title="Копировать" />
                    )}
                  </Space.Compact>
                </Form.Item>
              </>
            )}
            <Form.Item name="vid" label="Вид канала" rules={[{ required: true }]}>
              <Select options={dictsChannelVids} disabled={!!id} />
            </Form.Item>
            <Form.Item name="idx" label="Порядок">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            {providerOptions.length > 1 && (
              <>
                <Form.Item name="providerId" label="Провайдер">
                  <Select
                    placeholder="Не указан"
                    allowClear
                    options={providerOptions}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
                {selectedProvider?.url && (
                  <Form.Item label=" ">
                    <a href={selectedProvider.url} target="_blank" rel="noopener noreferrer">
                      Регистрация
                    </a>
                  </Form.Item>
                )}
                {selectedProvider?.description && (
                  <Form.Item label="Описание">
                    <Input.TextArea value={selectedProvider.description} readOnly rows={3} />
                  </Form.Item>
                )}
              </>
            )}
            {id && item.createdOn && (
              <Form.Item label="Дата создания">
                <Input value={new Date(item.createdOn).toLocaleString("ru-RU")} disabled />
              </Form.Item>
            )}
            <Form.Item label=" ">
              <Space>
                <Button type="primary" onClick={onSave} loading={loading}>Сохранить</Button>
                <Button onClick={() => onClose(false)}>Закрыть</Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Channel;
